import { Box, Stack, Typography } from "@mui/material";
import {
  DualLangInspectionsPermission,
  StellaInspectionsPermission,
} from "commons/enum/permission";
import { Dayjs } from "dayjs";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { AddDualLangInspectionNote } from "pages/dual-lang-inspection-note/add/add";
import { AddStellaInspectionNote } from "pages/stella-inspection-note/add-inspection-note/add-inspection-note";
import { useCallback } from "react";
import { DateRange } from "share-components/atomics/date-range";
import { ProductGroupSelection } from "share-components/group-selection/product-group-selection/product-group-selection";
import { ProductGroupSelectionKey } from "share-components/group-selection/product-group-selection/use-product-group-selection";
import { XAutoCompleteWithPagination } from "share-components/mui-wrap/auto-complete/x-auto-complete-with-pagination";
import { AddButton } from "share-components/mui-wrap/button/action-button";
import { SearchButton } from "share-components/mui-wrap/button/search-button";
import { XSearchTextField } from "share-components/mui-wrap/text-field/x-search-text-field";
import { PageContentContainer } from "share-components/templates/page/page-content-container";
import { PageFilterContainer } from "share-components/templates/page/page-filter-container";
import { PageHeaderContainer } from "share-components/templates/page/page-header-container";
import { searchInspectionNoteNumberOptionsWithPagination } from "./inspection-note-number-options";
import { InspectionNoteTable } from "./inspection-note-table";

export const InspectionNoteList = observer(() => {
  const {
    inspectionNoteManagement: {
      getInspectionNotes,
      pagination,
      queryParams: {
        number,
        setProp: setQueryParamProp,
        dateRangeFilter: { fromAsDayJs, toAsDayJs, setProp: setDateRangeProps },
        dealingName,
        name,
        code,
        barcode,
      },
    },
    stellaInspectionNoteManagement,
    dualLangInspectionNoteManagement,
  } = useStores();
  const onSearch = useCallback(async () => {
    pagination.setProp("pageIndex", 0);
    await getInspectionNotes();
  }, [getInspectionNotes, pagination]);

  const fromDateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setDateRangeProps("from", value ? value.toString() : null);
    },
    [setDateRangeProps],
  );

  const toDateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setDateRangeProps("to", value ? value.toString() : null);
    },
    [setDateRangeProps],
  );

  const setProductFilterProp = useCallback(
    (prop: ProductGroupSelectionKey, value: string) => {
      setQueryParamProp(prop as "dealingName" | "name", value);
    },
    [setQueryParamProp],
  );

  return (
    <>
      <PageHeaderContainer direction={"row"} justifyContent={"space-between"}>
        <Header></Header>
      </PageHeaderContainer>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="start"
        gap={4}
      >
        <Stack direction="column" gap={2}>
          <PageFilterContainer>
            <DateRange
              label={"Ngày kiểm nghiệm"}
              fromDate={fromAsDayJs}
              toDate={toAsDayJs}
              onFromDateChanged={fromDateChangedHandler}
              onToDateChanged={toDateChangedHandler}
            ></DateRange>
            <Box sx={{ minWidth: "200px" }}>
              <XAutoCompleteWithPagination
                label="Số phiếu"
                value={number}
                searchFunc={searchInspectionNoteNumberOptionsWithPagination}
                searchOnFocus={true}
                searchWhenRender={false}
                onValueChanged={(v) => {
                  setQueryParamProp("number", v?.value ?? "");
                }}
              />
            </Box>
          </PageFilterContainer>
          <PageFilterContainer>
            <ProductGroupSelection
              dealingNameSelected={dealingName}
              nameSelected={name}
              setSelectionProp={setProductFilterProp}
              searchWhenRender={false}
              codeSelected={code}
              visibleFields={["dealingName", "code", "name"]}
            ></ProductGroupSelection>
            <XSearchTextField
              label="Mã barcode"
              value={barcode}
              onChange={(e) =>
                setQueryParamProp("barcode", e.currentTarget.value)
              }
            />
            <SearchButton asyncAction={onSearch}></SearchButton>
          </PageFilterContainer>
        </Stack>
      </Stack>
      <PageContentContainer>
        <InspectionNoteTable></InspectionNoteTable>
      </PageContentContainer>

      <>
        {stellaInspectionNoteManagement.openNew && (
          <AddStellaInspectionNote></AddStellaInspectionNote>
        )}
      </>

      <>
        {dualLangInspectionNoteManagement.openNew && (
          <AddDualLangInspectionNote></AddDualLangInspectionNote>
        )}
      </>
    </>
  );
});

const Header = observer(() => {
  const {
    stellaInspectionNoteManagement: { setProp },
    dualLangInspectionNoteManagement: { setProp: setDualLangProp },
  } = useStores();
  return (
    <>
      <Stack>
        <Typography variant="subtitle2">Quản lý phiếu kiểm nghiệm</Typography>
      </Stack>
      <Stack direction={"row"} spacing={1} alignItems={"center"}>
        <AddButton
          accessPermission={[StellaInspectionsPermission.Create]}
          onClick={() => {
            setProp("openNew", true);
          }}
        >
          Thêm stella
        </AddButton>

        <AddButton
          accessPermission={[DualLangInspectionsPermission.Create]}
          onClick={() => {
            setDualLangProp("openNew", true);
          }}
        >
          Thêm song ngữ
        </AddButton>
      </Stack>
    </>
  );
});
